/**
 * 财务中心 - 交易明细页
 *
 * 实时查询某服务商结算账户下记账子单元的交易流水（近 13 个月），页面结构：
 * 1. 查询条件：服务商下拉（必选）+日期范围（必选，进页默认昨天单日）+子单元下拉单选（不选默认查全部，银行侧一次只能指定一个子单元）；
 *    进页服务商下拉加载成功且非空时默认选第一项并自动查询（下拉为空或加载失败不自动查）；日期边界前端先行拦截：跨度超过 100 天、
 *    结束日期晚于或等于当天、起始日期早于近 13 个月，拦截后不发起后端请求
 * 2. 明细列表 9 列：子单元名称/子单元编号/交易金额/交易方向/交易时间/收付方名称/收付方账号/记账方式/余额，
 *    交易时间由银行日期与时间字段合并格式化展示，金额与余额为银行原值原样展示不做换算，空值显示空白
 * 3. 滚动加载：银行单批返回一批记录与续传游标，首屏一批，滚动到表格底部自动带游标追加下一批直到取完；
 *    查询条件变化（点查询、切服务商）后缓冲区与游标全量重置从首屏重拉；
 *    "已加载 N 条"随追加累加，取完后提示"已全部加载"；银行续传中断时提示结果可能不完整；
 *    空结果展示"暂无交易明细"
 */
<template>
  <div class="finance-transactions-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item>财务中心</el-breadcrumb-item>
      <el-breadcrumb-item>交易明细</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="list-card" shadow="never">
      <div slot="header" class="card-header">
        <span>子单元交易明细</span>
      </div>

      <el-form :inline="true" :model="queryForm" size="small" class="filter-form">
        <el-form-item label="服务商" required>
          <el-select
            v-model="queryForm.accountNumber"
            filterable
            clearable
            placeholder="请选择服务商"
            style="width: 280px"
            @change="handleProviderChange"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p.accountNumber"
              :label="providerLabel(p)"
              :value="p.accountNumber"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围" required>
          <el-date-picker
            v-model="queryForm.dateRange"
            type="daterange"
            value-format="yyyyMMdd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 260px"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="子单元">
          <el-select
            v-model="queryForm.dmanbr"
            filterable
            clearable
            placeholder="不选默认查全部"
            :loading="subunitLoading"
            style="width: 260px"
          >
            <el-option
              v-for="s in subunitOptions"
              :key="s.dmanbr"
              :label="subunitLabel(s)"
              :value="s.dmanbr"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="listLoading" @click="handleQuery">查询</el-button>
        </el-form-item>
      </el-form>

      <div v-if="queried" class="load-info">
        <span>已加载 {{ rows.length }} 条<template v-if="rows.length && !hasMore">，已全部加载</template></span>
        <span v-if="scrollLoading" class="scroll-loading-text">加载中...</span>
        <el-alert
          v-if="truncated"
          type="warning"
          :closable="false"
          show-icon
          class="load-alert"
          title="银行续传中断，已加载结果可能不完整，可调整条件重新查询"
        ></el-alert>
      </div>

      <el-table
        ref="listTable"
        :data="rows"
        v-loading="listLoading"
        border
        size="small"
        height="520"
        empty-text="暂无交易明细"
      >
        <el-table-column prop="zDumNam" label="子单元名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ blankText(row.zDumNam) }}</template>
        </el-table-column>
        <el-table-column prop="zDumNbr" label="子单元编号" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ blankText(row.zDumNbr) }}</template>
        </el-table-column>
        <el-table-column prop="zTrsAmt" label="交易金额" min-width="120" align="right"></el-table-column>
        <el-table-column label="交易方向" width="100">
          <template slot-scope="{ row }">{{ trsDirText(row.zTrsDir) }}</template>
        </el-table-column>
        <el-table-column label="交易时间" width="150">
          <template slot-scope="{ row }">{{ trsTimeText(row) }}</template>
        </el-table-column>
        <el-table-column prop="zRpyNam" label="收/付方名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ blankText(row.zRpyNam) }}</template>
        </el-table-column>
        <el-table-column prop="zRpyAcc" label="收/付方账号" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ blankText(row.zRpyAcc) }}</template>
        </el-table-column>
        <el-table-column label="记账方式" width="150">
          <template slot-scope="{ row }">{{ autFlgText(row.zAutFlg) }}</template>
        </el-table-column>
        <el-table-column prop="zOnlBal" label="余额" min-width="130" align="right"></el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getFinanceProviderList, getFinanceSubunitList, getFinanceTransactionList } from '@/api/userMG';
import financeScrollList from './financeScrollList';

export default {
  name: 'FinanceTransactions',
  mixins: [financeScrollList],
  data() {
    return {
      // 服务商下拉选项
      providerOptions: [],
      // 当前服务商下子单元下拉选项（含已关闭行，历史交易可能涉及已关闭子单元）
      subunitOptions: [],
      subunitLoading: false,
      // 查询条件
      queryForm: {
        accountNumber: '',
        dateRange: null,
        dmanbr: ''
      },
      listLoading: false,
      // 记账方式编码与文案映射（银行编码原值展示）
      autFlgMap: {
        '1': '自动',
        '2': '调账',
        '3': '内部调配',
        '4': '关联公司卡',
        '5': '结算户入账分类条件',
        '6': '客户支付通知',
        '7': '薪智代发'
      }
    };
  },
  created() {
    this.loadProviders();
  },
  mounted() {
    this.bindTableScroll('listTable');
  },
  methods: {
    // 服务商下拉展示文案：账户名（账号）
    providerLabel(p) {
      return (p.accountName || '') + '（' + (p.accountNumber || '') + '）';
    },
    // 子单元下拉展示文案：名称（编号），默认子单元名称取不到时兜底展示
    subunitLabel(s) {
      const name = s.dmanam || (s.defaultSubunit ? '默认子单元' : '');
      return name + '（' + s.dmanbr + '）';
    },
    // 加载服务商下拉（招商银行记账子单元服务商账户）
    async loadProviders() {
      try {
        const res = await getFinanceProviderList();
        if (res && res.code === 0) {
          this.providerOptions = res.data || [];
          this.initDefaultQuery();
        } else {
          this.$message.error((res && res.msg) || '服务商列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      }
    },
    // 进页默认查询：服务商下拉非空时默认选第一项，日期默认昨天单日，联动加载子单元下拉并自动查询（下拉为空或加载失败不自动查）
    initDefaultQuery() {
      if (!this.providerOptions.length) {
        return;
      }
      this.queryForm.accountNumber = this.providerOptions[0].accountNumber;
      const yesterday = this.formatDate(new Date(Date.now() - 86400000));
      this.queryForm.dateRange = [yesterday, yesterday];
      this.loadSubunits();
      this.handleQuery();
    },
    // 切换服务商后重置子单元单选、联动加载子单元列表并从首屏重查明细
    handleProviderChange() {
      this.queryForm.dmanbr = '';
      this.subunitOptions = [];
      if (this.queryForm.accountNumber) {
        this.loadSubunits();
        this.handleQuery();
      }
    },
    // 加载当前服务商下子单元列表（含已关闭行，历史交易可能涉及已关闭子单元；响应为统一契约，下拉取 records）
    async loadSubunits() {
      this.subunitLoading = true;
      try {
        const res = await getFinanceSubunitList({ accountNumber: this.queryForm.accountNumber });
        if (res && res.code === 0) {
          const data = res.data || {};
          this.subunitOptions = data.records || [];
        } else {
          this.$message.error((res && res.msg) || '子单元列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      } finally {
        this.subunitLoading = false;
      }
    },
    // 查询前校验：服务商与日期必选、日期边界前端拦截，拦截后不调后端
    handleQuery() {
      if (!this.queryForm.accountNumber) {
        this.$message.warning('请先选择服务商');
        return;
      }
      const range = this.queryForm.dateRange;
      if (!range || range.length !== 2 || !range[0] || !range[1]) {
        this.$message.warning('请选择日期范围');
        return;
      }
      const beg = range[0];
      const end = range[1];
      const today = this.formatDate(new Date());
      if (end >= today) {
        this.$message.warning('结束日期必须早于当天（当天交易请次日查询）');
        return;
      }
      const spanDays = (this.parseDay(end) - this.parseDay(beg)) / 86400000;
      if (spanDays > 100) {
        this.$message.warning('日期跨度不能超过 100 天');
        return;
      }
      const threshold = new Date();
      threshold.setMonth(threshold.getMonth() - 13);
      if (beg < this.formatDate(threshold)) {
        this.$message.warning('起始日期不能早于近 13 个月');
        return;
      }
      this.loadFirstBatch();
    },
    // 当前查询条件参数（不含游标；续传游标由滚动加载逻辑自动并入）
    buildQueryParams() {
      const params = {
        accountNumber: this.queryForm.accountNumber,
        begDate: this.queryForm.dateRange[0],
        endDate: this.queryForm.dateRange[1]
      };
      if (this.queryForm.dmanbr) {
        params.dmanbrs = this.queryForm.dmanbr;
      }
      return params;
    },
    // 调列表接口拉一批（银行原始字段原样返回）；业务失败提示后返回 null，缓冲区保持不动
    async fetchBatch(params) {
      const res = await getFinanceTransactionList(params);
      if (res && res.code === 0) {
        return res.data || {};
      }
      this.$message.error((res && res.msg) || '交易明细查询失败');
      return null;
    },
    // 空值显示空白
    blankText(v) {
      return v === null || v === undefined ? '' : v;
    },
    // 交易日期时间：银行 yyyyMMdd + HHmmss 转 yyyy-MM-dd HH:mm:ss，空值显示空白
    trsTimeText(row) {
      const d = row.zTrsDat;
      const t = row.zTrsTim;
      if (!d || d.length !== 8) return '';
      let text = d.slice(0, 4) + '-' + d.slice(4, 6) + '-' + d.slice(6, 8);
      if (t && t.length === 6) {
        text += ' ' + t.slice(0, 2) + ':' + t.slice(2, 4) + ':' + t.slice(4, 6);
      }
      return text;
    },
    // 交易方向：C 贷（收入），D 借（支出）
    trsDirText(v) {
      if (v === 'C') return '贷（收入）';
      if (v === 'D') return '借（支出）';
      return '';
    },
    // 记账方式：银行编码映射文案，未知编码显示空白
    autFlgText(v) {
      return this.autFlgMap[v] || '';
    },
    // yyyyMMdd 字符串转毫秒数（本地时区）
    parseDay(s) {
      return new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8))).getTime();
    },
    // 日期转 yyyyMMdd 字符串
    formatDate(d) {
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return '' + d.getFullYear() + (m < 10 ? '0' + m : m) + (day < 10 ? '0' + day : day);
    }
  }
};
</script>

<style scoped>
.page-breadcrumb {
  margin-bottom: 16px;
}
.filter-form {
  margin-bottom: 4px;
}
.load-info {
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}
.scroll-loading-text {
  margin-left: 12px;
  color: #909399;
}
.load-alert {
  margin-top: 6px;
  padding: 4px 12px;
}
</style>



// WEBPACK FOOTER //
// src/views/finance/transactions.vue